import { hashNoise } from '../../world/noise.js';
import { footprintOf } from '../props.js';
import { terrainOf } from '../terrains.js';
import { COMPANY_LAST_STAND, COMPANY_MARCHERS } from '../company.js';
import { TRAIL_MAX } from './world-trails.js';
/**
 * The way into Shefira's Aire (ADR 0068 §2): the chasm she tore in the
 * glacier by La Brèche when she rose, a hole in the ground as long as she is
 * wide, a fire far down in it. It opens just outside the scar, open snow all
 * round its rim, a trail from the roads to its south edge.
 *
 * It is no exit and nobody walks into it: the Aire is an instance (ADR 0068
 * §1), entered by asking at the chasm's edge (`aerie_enter`), alone for the
 * story or as a raid. The world's zone keeps the whole chasm as a `gate`.
 */
/** The chasm, 15 tiles by 5, blocking, lying flat on the snow. */
export const AERIE_GATE_PROP = 'shefira-crevasse';
/** How far from the scar's edge the chasm may open, in tiles beyond its radius. */
const NEAR = { min: 2, max: 18 };
/** The trodden snow up to its south edge, from where the trail ends. */
const APPROACH = 4;
/** Open snow kept round the rim, in tiles: nothing stands there, all of it walked. */
const RIM = 2;
/**
 * Where the Company waits for the player on the rim once the last stand is
 * accepted (ADR 0068 §4), from the chasm's bottom-centre tile: Ser Givrecoeur
 * and Maëlle either side of the trail's end, the others along the south edge
 * and at both ends, looking into the chasm. In the order of `COMPANY_MARCHERS`.
 */
const MUSTER_SPOTS = [
    { dx: 3, dy: 2, facing: 'south' },
    { dx: -3, dy: 2, facing: 'south' },
    { dx: -6, dy: 1, facing: 'north' },
    { dx: 6, dy: 1, facing: 'north' },
    { dx: -8, dy: -2, facing: 'east' },
    { dx: 8, dy: -2, facing: 'west' },
];
/**
 * The chasm by La Brèche: the spot of snow nearest the scar where its
 * footprint, a rim of open snow round it and the way up to its south edge
 * lie on snow, and a trail from the roads can reach. Null when the snow has
 * no room for it there, which a test forbids.
 */
export function placeAerieGate(fields, occupancy, trails, breach, name) {
    const { centre, radius } = breach;
    const candidates = [];
    const reach = radius + NEAR.max;
    for (let y = centre.y - reach; y <= centre.y + reach; y += 1) {
        for (let x = centre.x - reach; x <= centre.x + reach; x += 1) {
            if (!fields.inside(x, y) || fields.biomeAt(x, y) !== 'snow')
                continue;
            const d = Math.hypot(x - centre.x, y - centre.y);
            if (d < radius + NEAR.min || d > radius + NEAR.max)
                continue;
            if (!groundOpen(fields, { prop: AERIE_GATE_PROP, x, y, flip: false }))
                continue;
            const far = trails.reach[fields.index(x, y + APPROACH)] ?? 0xffff;
            if (far > TRAIL_MAX - 20)
                continue;
            candidates.push({ x, y, score: -d - 0.2 * far + hashNoise(x, y, fields.seed + 68) });
        }
    }
    candidates.sort((a, b) => b.score - a.score);
    for (const { x, y } of candidates) {
        const chasm = { prop: AERIE_GATE_PROP, x, y, flip: false };
        if (!occupancy.fits(chasm, true))
            continue;
        occupancy.take(chasm);
        const trailhead = { x, y: y + APPROACH };
        if (trails.carve(trailhead, 3, TRAIL_MAX) === null) {
            occupancy.release(chasm);
            continue;
        }
        // Trodden snow from the trail's end to the south edge, three wide.
        for (let py = y + 1; py < trailhead.y; py += 1) {
            for (const px of [x - 1, x, x + 1]) {
                const i = fields.index(px, py);
                if (fields.locked[i] !== 1 && terrainOf(fields.terrain[i] ?? '.').walkable) {
                    fields.terrain[i] = 'd';
                    trails.clearBrush(px, py);
                }
            }
        }
        const box = footprintOf(chasm);
        // The rim stays open snow all round: no tree, rock or den on it.
        for (let py = box.y0 - RIM; py <= box.y1 + RIM; py += 1) {
            for (let px = box.x0 - RIM; px <= box.x1 + RIM; px += 1) {
                const inside = px >= box.x0 && px <= box.x1 && py >= box.y0 && py <= box.y1;
                if (!inside)
                    occupancy.reserveDisc(px, py, 0);
            }
        }
        occupancy.reserveDisc(x, y + 2, 3);
        return {
            gate: {
                area: { x: box.x0, y: box.y0, width: box.x1 - box.x0 + 1, height: box.y1 - box.y0 + 1 },
                arrival: { x, y: y + 1 },
                landmark: { name, centre: { x, y: y - 2 }, radius: 9 },
            },
            props: [chasm],
            folk: COMPANY_MARCHERS.map((mob, i) => {
                const spot = MUSTER_SPOTS[i] ?? MUSTER_SPOTS[0];
                return {
                    mob: mob,
                    x: x + spot.dx,
                    y: y + spot.dy,
                    count: 1,
                    radius: 0,
                    facing: spot.facing,
                    appearsOn: COMPANY_LAST_STAND,
                };
            }),
        };
    }
    return null;
}
/**
 * Open snow where the chasm and its approach go: the footprint with its rim
 * round it, and the strip up to its south edge. Snow alone, never locked: no
 * cliff, no road, no other ground under it.
 */
function groundOpen(fields, chasm) {
    const box = footprintOf(chasm);
    const boxes = [
        { x0: box.x0 - RIM, x1: box.x1 + RIM, y0: box.y0 - RIM, y1: box.y1 + RIM },
        { x0: chasm.x - 2, x1: chasm.x + 2, y0: box.y1 + 1, y1: box.y1 + APPROACH + 1 },
    ];
    for (const b of boxes) {
        for (let ty = b.y0; ty <= b.y1; ty += 1) {
            for (let tx = b.x0; tx <= b.x1; tx += 1) {
                if (!fields.inside(tx, ty))
                    return false;
                const i = fields.index(tx, ty);
                if (fields.terrain[i] !== 'n' || fields.locked[i] === 1)
                    return false;
            }
        }
    }
    return true;
}
//# sourceMappingURL=world-aerie.js.map